import * as React from 'react';
import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import {
    AppBar,
    Button,
    Checkbox,
    Dialog,
    FormControlLabel,
    Toolbar,
    Tooltip
} from '@material-ui/core';
import * as Styled from './styled';
import { LoadingDiv } from 'containers/Home/Home';
import { RootStoreContext } from '../../App';
import BulkEntry from './BulkEntry';
import { FIELDS } from 'api/types/types';
import Pagination from 'containers/Pagination/Pagination';

interface Fields {
    [key: string]: {
        labelKey: string;
        minWidth?: string;
        width?: string;
        maxChar?: number;
        ariaLabelAsAbbreviation?: boolean;
    }
}

export const FIELDS_PROPS: Fields = {
    [FIELDS.ROW]: {labelKey: 'row_number', minWidth: '40px', width: '40px'},
    [FIELDS.WORK_DATE]: {labelKey: 'work_date', minWidth: '90px', width: '90px', maxChar: 10},
    [FIELDS.DURATION]: {labelKey: 'duration', minWidth: '50px', width: '50px'},
    [FIELDS.MATTER_NUMBER]: {labelKey: 'matter_number', maxChar: 24, ariaLabelAsAbbreviation: true},
    [FIELDS.PHASE]: {labelKey: 'phase', ariaLabelAsAbbreviation: true},
    [FIELDS.TASK]: {labelKey: 'task', ariaLabelAsAbbreviation: true},
    [FIELDS.ACTIVITY]: {labelKey: 'activity', ariaLabelAsAbbreviation: true},
    [FIELDS.FF_TASK]: {labelKey: 'ff_task', ariaLabelAsAbbreviation: true},
    [FIELDS.FF_ACT]: {labelKey: 'ff_act', ariaLabelAsAbbreviation: true},
    [FIELDS.NARRATIVE]: {labelKey: 'narrative', minWidth: 'calc(100% - 102px)'}
};

function BulkEntriesView() {
    const rootStore = useContext(RootStoreContext);
    const titleRef = useRef<HTMLElement>(null);
    const bulkEntriesListRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation('timeentries');
    const {
        bulkEntries,
        filteredBulkEntries,
        paginatedBulkEntries,
        invalidRows,
        expandedIds,
        isBulkEntriesViewOpen,
        showOnlyInvalidRows,
        currentPage,
        entriesPerPage,
        loading,
        setPageNum,
        setEntriesPerPage,
        closeBulkEntriesView,
        submitBulkEntries,
        toggleOnlyShowInvalidRows
    } = rootStore.bulkUploadStore;

    useEffect(() => {
        const { EpochConfigWorkLocaleEnabled, EpochConfigActionCodesRequired } = rootStore.appStore.features;
        if (EpochConfigWorkLocaleEnabled) {
            FIELDS_PROPS[FIELDS.WORK_LOCALE] = {labelKey: 'work_locale'};
        }
        if (EpochConfigActionCodesRequired)  {
            FIELDS_PROPS[FIELDS.ACTION_CODE] = {labelKey: 'action_code'};
        }
        if (rootStore.appStore.isProject) {
            FIELDS_PROPS[FIELDS.BILLABLE] = {labelKey: 'billable'};
            FIELDS_PROPS[FIELDS.INCIDENT_NUMBER] = {labelKey: 'incident_number'};
        }
    }, []);

    const changeEntriesPerPage = (n: number) => {
        setEntriesPerPage(n);
        changePage(1);
    }

    const changePage = (pageNum: number) => {
        setPageNum(pageNum);
        bulkEntriesListRef.current!.scrollTop = 0;
    }

    const handleClose = async () => {
        rootStore.appStore.setPreviousActiveElement();
        const confirm = await rootStore.confirmStore.confirm('dialog.confirm.message.cancel');
        if (confirm) {
            closeBulkEntriesView();
        }
    }

    const handleSubmit = async () => {
        await submitBulkEntries();
        if (titleRef.current) {
            titleRef.current.focus();
        }
    }

    return (
        <Dialog
            open={isBulkEntriesViewOpen}
            onClose={handleClose}
            onRendered={() => titleRef.current!.focus()}
            fullScreen={true}
        >
            <AppBar style={{ position: 'sticky'}}>
                <Toolbar>
                    <Styled.HeaderTitle
                        innerRef={titleRef}
                        tabIndex={0}
                        aria-label={`
                            ${t('bulk_upload.view.header.title')},
                            ${t('bulk_upload.view.sub_header.total')} ${bulkEntries.length},
                            ${t('bulk_upload.view.sub_header.invalid')} ${invalidRows.size}
                        `}
                    >
                        {t('bulk_upload.view.header.title')}
                    </Styled.HeaderTitle>
                    <Button color="inherit" onClick={handleClose}>
                        {t('close', { ns: 'common' })}
                    </Button>
                    <Tooltip title={invalidRows.size > 0 ? t('bulk_upload.view.header.action.submit.disabled.tooltip') : ''}>
                        <span>
                            <Button 
                                color="inherit"
                                onClick={handleSubmit}
                                disabled={invalidRows.size > 0}
                            >
                                {t('bulk_upload.view.header.action.submit.label')}
                            </Button>
                        </span>
                    </Tooltip>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showOnlyInvalidRows}
                                onChange={toggleOnlyShowInvalidRows}
                                name="toggle_show"
                                disabled={invalidRows.size === 0}
                                inputProps={{
                                    'aria-label': t('bulk_upload.view.header.checkbox.show_unvalid')
                                }}
                            />
                        }
                        label={t('bulk_upload.view.header.checkbox.show_unvalid')}
                        style={{
                            position: 'absolute',
                            right: '24px'
                        }}
                    />
                </Toolbar>
            </AppBar>
            <Styled.SubHeaderContainer>
                <Styled.SubHeaderText>
                    <span>{`${t('bulk_upload.view.sub_header.total')} ${bulkEntries.length}`}</span>
                    <span>{`${t('bulk_upload.view.sub_header.invalid')} ${invalidRows.size}`}</span>
                </Styled.SubHeaderText>
                <Pagination
                    entriesPerPage={entriesPerPage}
                    currentPage={currentPage}
                    entriesLength={filteredBulkEntries.length}
                    changeEntriesPerPage={changeEntriesPerPage}
                    onPageChange={changePage}
                />
            </Styled.SubHeaderContainer>
            <Styled.ListContainer innerRef={bulkEntriesListRef}>
                {paginatedBulkEntries.map((obj, index) =>
                    <BulkEntry 
                        key={index}
                        data={obj}
                        expanded={expandedIds.has(obj.entry[FIELDS.ROW])}
                    />
                )}
                {loading && LoadingDiv(t('bulk_upload.view.header.action.submit.loading'), 0.5)}
            </Styled.ListContainer>
        </Dialog>
    );
}

export default observer(BulkEntriesView);